// The Tracking quantities header filters (owner, 2026-09-20: "filter by
// all headers at all levels of the tree").
//
// THE FILTERING IS DONE BY THE SERVER, not by the table. The tree is lazy -
// a collapsed category's elements are not in the browser - so a filter that
// ran here could only ever filter what happened to be loaded, which is the
// trap the backend note describes: it would look right and be wrong. These
// dropdowns therefore only COLLECT a filter and hand it up; none of them
// carries an `onFilter`, because antd's own filtering must not also run.
//
// They are separate from the table so the shape can be tested without
// rendering one, and so the three menus cannot drift apart.
import React, { useState } from "react";
import { Button, Checkbox, Input, InputNumber, Space } from "antd";
import { FilterFilled, SearchOutlined } from "@ant-design/icons";
// The same three tokens the trees use. Local rather than imported,
// because trackingTheme carries status and system colours, not chrome.
const T = { muted: "#6B7280", ink: "#111827", line: "#E5E7EB", accent: "#1f3a5f" };

// `rules` and `gate` are the rule builder's (./RuleBuilder). They live in
// the SAME object as the header filters because they are the same filter:
// one object, resolved once by the server for the count, the select-all,
// the preview and the write.
export const EMPTY = { name: "", levels: [], statuses: [], ranges: {}, rules: [], gate: "AND" };

/** Is anything actually being filtered? Used for the header icon and for
 *  deciding whether the server takes the fast roll-up path at all. */
export function anyActive(f) {
  if (!f) return false;
  if (f.name) return true;
  if ((f.levels || []).length) return true;
  if ((f.statuses || []).length) return true;
  if ((f.rules || []).length) return true;
  const set = (x) => x !== null && x !== undefined && x !== "";
  return Object.values(f.ranges || {}).some((r) => !!r && (set(r.min) || set(r.max)));
}

/** What the server is sent: nulls rather than empty strings, so "cleared"
 *  and "zero" cannot be confused - a min of 0 is a real filter. */
/** The way back from what a PAGE saved to what the column headers hold.
 *
 * forServer() drops empty fields and omits `rules`/`gate` entirely when there
 * are no rules, so a saved filter is a SUBSET of the header shape - restoring
 * it by spreading alone would leave `ranges` or `rules` undefined and every
 * reader does `(f.rules || [])` on the way in, which is the normalise-at-the
 * -call trap. Fill the shape here, once, so nothing downstream has to guess.
 *
 * Returns EMPTY for nothing saved, so "this page has no filter" and "this page
 * was never saved" land on the same, visible state rather than on undefined.
 */
export function fromSaved(f) {
  if (!f || typeof f !== 'object') return { ...EMPTY, ranges: {}, levels: [], statuses: [], rules: [] };
  const ranges = {};
  for (const [k, r] of Object.entries(f.ranges || {})) {
    if (!r || typeof r !== 'object') continue;
    const min = r.min === null || r.min === undefined ? "" : r.min;
    const max = r.max === null || r.max === undefined ? "" : r.max;
    if (min !== "" || max !== "") ranges[k] = { min, max };
  }
  return {
    name: typeof f.name === 'string' ? f.name : "",
    levels: Array.isArray(f.levels) ? f.levels.filter(Boolean) : [],
    statuses: Array.isArray(f.statuses) ? f.statuses.filter(Boolean) : [],
    ranges,
    rules: Array.isArray(f.rules) ? f.rules : [],
    gate: f.gate === 'OR' || f.gate === 'NOT' ? f.gate : "AND",
  };
}

export function forServer(f) {
  if (!anyActive(f)) return null;
  const ranges = {};
  for (const [k, r] of Object.entries(f.ranges || {})) {
    const min = r.min === "" || r.min === undefined ? null : r.min;
    const max = r.max === "" || r.max === undefined ? null : r.max;
    if (min !== null || max !== null) ranges[k] = { min, max };
  }
  return {
    name: f.name || "", levels: f.levels || [], statuses: f.statuses || [], ranges,
    ...((f.rules || []).length ? { rules: f.rules, gate: f.gate || "AND" } : {}),
  };
}

const icon = (on) => <FilterFilled style={{ color: on ? T.accent : undefined }} />;

const Foot = ({ onApply, onClear }) => (
  <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 8px", borderTop: `1px solid ${T.line}` }}>
    <Button size="small" type="link" onClick={onClear}>Clear</Button>
    <Button size="small" type="primary" onClick={onApply}>Filter</Button>
  </div>
);

// THE DROPDOWN BODIES LIVE HERE, NOT INSIDE `filterDropdown`.
//
// They were defined inside it, as `const Box = () => …; return <Box />`,
// which makes a NEW COMPONENT TYPE on every render of the table - so React
// unmounts the old one and mounts a fresh one, and its state goes back to
// the prop it started from. Tick a level, let anything re-render the
// table, press Filter, and the box sends the value it had BEFORE the
// tick: nothing. The browser check caught it on a real page after every
// unit test had passed, because the unit tests never re-rendered between
// the tick and the press.
function TextBox({ value, onChange, confirm }) {
  const [v, setV] = useState(value || "");
  const apply = () => { onChange(v.trim()); confirm({ closeDropdown: true }); };
  return (
    <div style={{ width: 240 }}>
      <div style={{ padding: 8 }}>
        <Input
          size="small" autoFocus allowClear prefix={<SearchOutlined style={{ color: T.muted }} />}
          placeholder="Name contains" value={v}
          onChange={(e) => setV(e.target.value)} onPressEnter={apply}
        />
      </div>
      <Foot onApply={apply} onClear={() => { onChange(""); confirm({ closeDropdown: true }); }} />
    </div>
  );
}

function PickBox({ options, value, onChange, confirm, width }) {
  const [v, setV] = useState(value || []);
  // Nothing ticked and everything ticked mean the same thing - no filter -
  // and it is stored as the empty list so the two cannot disagree.
  const apply = () => { onChange(v.length === options.length ? [] : v); confirm({ closeDropdown: true }); };
  return (
    <div style={{ width }}>
      <div style={{ padding: 8, maxHeight: 260, overflow: "auto" }}>
        <Checkbox.Group value={v} onChange={setV}>
          <Space direction="vertical" size={2}>
            {options.map((o) => <Checkbox key={o.value} value={o.value}>{o.label}</Checkbox>)}
          </Space>
        </Checkbox.Group>
        {!options.length && <span style={{ fontSize: 12, color: T.muted }}>Nothing to filter by yet</span>}
      </div>
      <Foot onApply={apply} onClear={() => { onChange([]); confirm({ closeDropdown: true }); }} />
    </div>
  );
}

function RangeBox({ unit, value, onChange, confirm }) {
  const r = value || {};
  const [min, setMin] = useState(r.min === null || r.min === undefined ? "" : r.min);
  const [max, setMax] = useState(r.max === null || r.max === undefined ? "" : r.max);
  const apply = () => { onChange({ min, max }); confirm({ closeDropdown: true }); };
  return (
    <div style={{ width: 240 }}>
      <Space style={{ padding: 8 }} size={6}>
        <InputNumber size="small" style={{ width: 100 }} placeholder={`Min ${unit || ""}`.trim()}
          value={min} onChange={(x) => setMin(x === null ? "" : x)} onPressEnter={apply} />
        <span style={{ color: T.muted }}>to</span>
        <InputNumber size="small" style={{ width: 100 }} placeholder={`Max ${unit || ""}`.trim()}
          value={max} onChange={(x) => setMax(x === null ? "" : x)} onPressEnter={apply} />
      </Space>
      <Foot onApply={apply} onClear={() => { onChange({ min: "", max: "" }); confirm({ closeDropdown: true }); }} />
    </div>
  );
}

/** Name contains. Text, never a pattern - the server escapes it. */
export function textFilter(value, onChange) {
  return {
    filtered: !!value,
    filterIcon: () => icon(!!value),
    filterDropdown: ({ confirm }) => (
      <TextBox value={value} onChange={onChange} confirm={confirm} />
    ),
  };
}

/** A multi-select over a fixed set: Level, Status. Nothing ticked means no
 *  filter, which is the same thing as everything ticked and is stored as
 *  the empty list so the two cannot disagree. */
export function pickFilter(options, value, onChange, { width = 220 } = {}) {
  const on = (value || []).length > 0;
  return {
    filtered: on,
    filterIcon: () => icon(on),
    filterDropdown: ({ confirm }) => (
      <PickBox options={options} value={value} onChange={onChange} confirm={confirm} width={width} />
    ),
  };
}

/** Min / max on one measure. The bounds are INCLUSIVE and the placeholder
 *  says the unit, because "Length 10" means nothing without one. */
export function rangeFilter(unit, value, onChange) {
  const r = value || {};
  const set = (x) => x !== null && x !== undefined && x !== "";
  const on = set(r.min) || set(r.max);
  return {
    filtered: on,
    filterIcon: () => icon(on),
    filterDropdown: ({ confirm }) => (
      <RangeBox unit={unit} value={r} onChange={onChange} confirm={confirm} />
    ),
  };
}

/**
 * A rule set in words, for the Rules button, the Filtered banner and the
 * status confirmation - one wording in all three. Each rule carries its own
 * gate now (as on the Timeline page), so "joined by X" became false the
 * moment two rules chose different gates.
 */
export function rulesInWords(f) {
  const rules = (f && f.rules) || [];
  if (!rules.length) return "";
  const gates = [...new Set(rules.map((r) => r.gate || f.gate || "AND"))];
  return `${rules.length} rule${rules.length === 1 ? "" : "s"} (${gates.join(" + ")})`;
}
